import { UserProfileRole } from '@@types/api.types';
import { Button } from '@components/ui/button';
import { useGetCurrentUser, useRefreshImpersonation, useStopImpersonation } from '@queries/users.query';
import { routes } from '@routes';
import {
  formatRemainingTime,
  getImpersonationRemainingSeconds,
  isImpersonationExpiringSoon,
} from '@stores/impersonation.store';
import { AlertTriangleIcon, BanIcon, ClockArrowUpIcon, ClockIcon } from 'lucide-react';
import type React from 'react';
import { useCallback, useEffect, useState } from 'react';

function formatRole(role: string | null): string {
  if (!role) return 'User';
  if (role === UserProfileRole.EXPERT) return 'Expert';
  if (role === UserProfileRole.INTELLIGENCE) return 'Intelligence';
  if (role === UserProfileRole.INNOVATOR) return 'Innovator';
  if (role === UserProfileRole.ADMIN) return 'Admin';
  return role;
}

export const ImpersonationBanner: React.FC = () => {
  const { data: user } = useGetCurrentUser();
  const { mutateAsync: stopImpersonation, isPending: isStopPending } = useStopImpersonation();
  const { mutateAsync: refreshImpersonation, isPending: isRefreshPending } = useRefreshImpersonation();
  const [remainingSeconds, setRemainingSeconds] = useState(0);

  const impersonation = user?.impersonation;
  const isExpiringSoon = isImpersonationExpiringSoon(impersonation);

  // Update remaining time every second
  useEffect(() => {
    if (!impersonation?.expiresAt) return;

    const updateRemaining = () => {
      const seconds = getImpersonationRemainingSeconds(impersonation);
      setRemainingSeconds(seconds);

      // Auto-redirect when timer expires
      if (seconds <= 0) {
        window.location.replace(routes.adminUsers.path);
      }
    };

    updateRemaining();
    const interval = setInterval(updateRemaining, 1000);

    return () => clearInterval(interval);
  }, [impersonation]);

  useEffect(() => {
    if (impersonation) {
      document.documentElement.style.setProperty('--impersonation-banner-height', '3rem');
      return () => {
        document.documentElement.style.removeProperty('--impersonation-banner-height');
      };
    }

    document.documentElement.style.removeProperty('--impersonation-banner-height');
  }, [impersonation]);

  const handleStopImpersonation = useCallback(async () => {
    await stopImpersonation(undefined, {
      onSuccess: () => {
        window.location.replace(routes.adminUsers.path);
      },
    });
  }, [stopImpersonation]);

  if (!impersonation) {
    return null;
  }

  return (
    <>
      <div className="fixed top-0 left-0 right-0 z-9999 h-12 w-full">
        <div
          role="alert"
          aria-live="polite"
          className="h-full bg-red-600 text-white px-4 py-2 shadow-lg flex items-center justify-center gap-3 mx-auto"
        >
          <AlertTriangleIcon className="size-4 shrink-0" />
          <span className="text-sm font-medium">
            Impersonating {formatRole(user?.profile?.role ?? null)}: {user?.profile?.fullName ?? 'Unknown'}
          </span>

          {isExpiringSoon && (
            <>
              <span className="flex items-center gap-1 text-xs text-yellow-200 animate-pulse">
                Expires in <ClockIcon className="size-4" />
                {formatRemainingTime(remainingSeconds)}
              </span>
              <Button
                onClick={() => refreshImpersonation()}
                size="sm"
                variant="ghost"
                disabled={isRefreshPending}
                className="text-white hover:bg-red-700"
              >
                {<ClockArrowUpIcon className="size-4" />}
                <span>Extend</span>
              </Button>
            </>
          )}

          <Button
            onClick={handleStopImpersonation}
            size="sm"
            variant="destructive"
            disabled={isStopPending}
            className=""
          >
            {<BanIcon className="size-4" />}
            <span>Stop</span>
          </Button>
        </div>
      </div>
      {/* Spacer to push content below fixed banner */}
      <div
        className="h-12"
        aria-hidden="true"
      />
    </>
  );
};
